import React, {FormEventHandler, useState} from 'react';
import {Link, withRouter, WithRouterProps} from 'react-router';
import {Modal, Button, SvgIcon, Title, FormikCheckbox} from '@shipwell/shipwell-ui';
import {compose} from 'recompose';
import {Field, Formik} from 'formik';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {TransportationMode} from '@shipwell/genesis-sdk';
import WithStatusToasts, {WithStatusToastProps} from 'App/components/withStatusToasts';
import {useUserMe} from 'App/data-hooks';
import useToggle from 'App/utils/hooks/useToggle';
import useDisconnectChRobinsonMutation from 'App/containers/integrations/details/components/chRobinsonDetails/hooks/mutations';
import {
  useChRobinsonFreightQuery,
  useChRobinsonUpdateMutation
} from 'App/containers/chrobinson/hooks/useChRobinsonMutation';
import FormFooter from 'App/formComponents/formSections/formFooter';

interface CHRobinsonPropsTypes extends WithStatusToastProps, WithRouterProps {}

const CHRobinsonDetails = (props: CHRobinsonPropsTypes) => {
  const {intCarrierApiChrLtl} = useFlags() || {};
  const userMeQuery = useUserMe();
  const [showDisconnectModal, toggleDisconnectModal] = useToggle();
  const [modes, setModes] = useState<TransportationMode[]>([]);

  useChRobinsonFreightQuery({
    onSuccess: (data) => {
      setModes(data?.modes || []);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const {mutate: disconnectChRobinsonAccount} = useDisconnectChRobinsonMutation({
    onSuccess() {
      props.router.goBack();
    },
    onError(error) {
      props.setError('Error!', error.message);
    }
  });

  const handleDisconnect = () => {
    const companyId = userMeQuery?.data?.company?.id;
    if (companyId) {
      disconnectChRobinsonAccount();
    }
  };

  const DisconnectModal = () => (
    <Modal
      title="Disconnect Integration"
      className="w-96 align-middle"
      show={showDisconnectModal}
      onClose={() => toggleDisconnectModal()}
      footerComponent={null}
      variant="warning"
    >
      <div className="flex flex-row items-center px-2 py-1">
        <span>You will no longer see the rates from this carrier.</span>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" type="button" onClick={() => toggleDisconnectModal()}>
          Cancel
        </Button>
        <Button type="button" variant="primary" color="warning" onClick={() => handleDisconnect()}>
          Yes, Disconnect
        </Button>
      </div>
    </Modal>
  );

  const DisconnectButton = () => (
    <div className="flex justify-end pb-6">
      <Button title="disconnect" variant="secondary" onClick={() => toggleDisconnectModal()}>
        Disconnect
      </Button>
    </div>
  );

  const {mutate: editCapacityProviders} = useChRobinsonUpdateMutation({
    onSuccess() {
      props.router.goBack();
    }
  });

  const validateCheckboxes = (values: {ltl: boolean; ftl: boolean}) => {
    return !values.ltl && !values.ftl ? 'Select at least one option' : false;
  };

  const onCancel = () => {
    props.router.goBack();
  };

  const handleUpdateValues = (values: {ltl: boolean; ftl: boolean}) => {
    if (!intCarrierApiChrLtl) {
      return;
    }

    const modes = [values.ltl && TransportationMode.Ltl, values.ftl && TransportationMode.Ftl].filter(
      (x) => !!x
    ) as TransportationMode[];
    editCapacityProviders(modes);
  };

  return (
    <div>
      <DisconnectButton />
      <DisconnectModal />
      <div className="flex flex-col divide-solid rounded border border-sw-border bg-sw-background-component p-6">
        <div className="mb-4 flex w-full items-center">
          <div className="flex w-full">
            <div className="flex flex-col">
              <div className="mb-5">
                For carrier information, please visit the{' '}
                <Link to="/carriers?page=1&q=chrobinson">CH Robinson carrier page</Link>.
              </div>
              <div className="w-full">
                <SvgIcon name="CheckCircleFilled" color="$sw-success" className="float-left" />
                <span className="float-left mt-1 text-sw-success">You are connected with CH Robinson.</span>
              </div>
              {intCarrierApiChrLtl && (
                <Formik
                  enableReinitialize
                  initialValues={{
                    ltl: modes.includes(TransportationMode.Ltl),
                    ftl: modes.includes(TransportationMode.Ftl)
                  }}
                  onSubmit={handleUpdateValues}
                >
                  {(innerProps: {
                    values: {ltl: boolean; ftl: boolean};
                    dirty?: boolean;
                    isValid?: boolean;
                    isSubmitting?: boolean;
                    handleSubmit: FormEventHandler<HTMLFormElement>;
                  }) => {
                    return (
                      <form noValidate onSubmit={innerProps.handleSubmit}>
                        <div className="flex flex-col pt-5">
                          <Title variant="formTitle" className="mb-3">
                            Shipping Mode:
                          </Title>
                          <div className="flex flex-row">
                            <div className="flex w-96 content-start items-start">
                              <Field
                                name="ltl"
                                label="LTL"
                                component={FormikCheckbox}
                                data-testId="ltl-input"
                                fixedHeight={false}
                                validate={() => validateCheckboxes(innerProps.values)}
                              />
                            </div>
                            <div className="flex w-96 content-start items-start">
                              <Field
                                name="ftl"
                                label="FTL"
                                component={FormikCheckbox}
                                data-testId="ftl-input"
                                fixedHeight={false}
                                validate={() => validateCheckboxes(innerProps.values)}
                              />
                            </div>
                          </div>
                        </div>
                        {innerProps.dirty && (
                          <FormFooter primaryActionName={'Save Settings'} onCancel={() => onCancel()} />
                        )}
                      </form>
                    );
                  }}
                </Formik>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default compose<CHRobinsonPropsTypes, unknown>(withRouter, WithStatusToasts)(CHRobinsonDetails);
